<template>
  <div class="meet-scheduler min-w-0 overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-[0_20px_55px_rgba(15,23,42,.10)] sm:rounded-[22px]">
    <div class="grid" :class="compactLayout ? '' : 'lg:min-h-[590px] lg:grid-cols-[270px_minmax(0,1fr)]'">
      <aside class="border-b border-zinc-200 p-5" :class="compactLayout ? '' : 'sm:p-7 lg:border-b-0 lg:border-r lg:p-8'">
        <img v-if="logoUrl" :src="logoUrl" alt="" class="h-11 max-w-[175px] object-contain">
        <div v-else class="flex h-11 w-11 items-center justify-center rounded-xl text-white" :style="{ backgroundColor: accentColor }">
          <PhoneArrowUpRightIcon class="h-5 w-5" />
        </div>
        <p class="mt-5 text-sm font-semibold text-zinc-500 sm:mt-7">One-on-one Meet</p>
        <h2 class="mt-1 text-2xl font-bold tracking-tight text-zinc-950">{{ settings.title || 'Private strategy call' }}</h2>
        <p class="mt-3 text-sm leading-6 text-zinc-600">{{ settings.description || 'Choose a time that works for you.' }}</p>
        <div class="mt-5 space-y-3 text-sm font-semibold text-zinc-500 sm:mt-7 sm:space-y-4">
          <p class="flex items-center gap-2.5"><ClockIcon class="h-5 w-5" />{{ durationMinutes }} min</p>
          <p class="flex items-start gap-2.5"><VideoCameraIcon class="mt-0.5 h-5 w-5 shrink-0" /><span>{{ settings.locationLabel || 'Video conferencing details provided upon confirmation.' }}</span></p>
          <p v-if="priceCents" class="flex items-center gap-2.5"><CreditCardIcon class="h-5 w-5" />{{ priceLabel }}</p>
        </div>
      </aside>

      <section class="min-w-0 p-4" :class="compactLayout ? '' : 'sm:p-6 lg:p-8'">
        <h3 class="text-xl font-bold text-zinc-950">Select a Date &amp; Time</h3>
        <div class="mt-5 grid min-w-0 gap-6" :class="compactLayout ? '' : 'sm:mt-7 xl:grid-cols-[minmax(330px,1fr)_190px] xl:gap-7'">
          <div>
            <div class="flex items-center justify-between">
              <button type="button" class="rounded-full p-2 text-zinc-500 transition hover:bg-zinc-100" aria-label="Previous month" @click="moveMonth(-1)"><ChevronLeftIcon class="h-5 w-5" /></button>
              <p class="text-base font-semibold text-zinc-800">{{ monthLabel }}</p>
              <button type="button" class="rounded-full p-2 transition" :style="{ color: accentColor, backgroundColor: accentSoft }" aria-label="Next month" @click="moveMonth(1)"><ChevronRightIcon class="h-5 w-5" /></button>
            </div>
            <div class="mt-4 grid grid-cols-7 gap-y-2 text-center sm:mt-5">
              <span v-for="day in weekdayLabels" :key="day" class="pb-2 text-[10px] font-semibold uppercase tracking-wide text-zinc-500 sm:text-[11px]">{{ day }}</span>
              <button
                v-for="cell in calendarCells"
                :key="cell.key"
                type="button"
                class="mx-auto flex items-center justify-center rounded-full transition"
                :class="[
                  compactLayout ? 'h-9 w-9 text-xs' : 'h-9 w-9 text-xs sm:h-10 sm:w-10 sm:text-sm',
                  calendarDayClass(cell),
                ]"
                :style="calendarDayStyle(cell)"
                :disabled="!cell.available"
                @click="selectDate(cell)"
              >
                {{ cell.label }}
              </button>
            </div>
            <div class="mt-6 border-t border-zinc-100 pt-5 sm:mt-8">
              <p class="text-sm font-bold text-zinc-900">Time zone</p>
              <p class="mt-2 flex items-center gap-2 text-sm text-zinc-600"><GlobeAltIcon class="h-4 w-4" />{{ timezoneLabel }}</p>
            </div>
          </div>

          <div :class="compactLayout ? '' : 'xl:border-l xl:border-zinc-100 xl:pl-6'">
            <p class="min-h-10 text-sm font-semibold leading-5 text-zinc-700">{{ selectedDateLabel }}</p>
            <div v-if="loading" class="mt-3 grid grid-cols-2 gap-2" :class="compactLayout ? '' : 'xl:block xl:space-y-2'"><div v-for="index in 6" :key="index" class="h-12 animate-pulse rounded-lg bg-zinc-100" /></div>
            <div v-else-if="selectedTimes.length" class="meet-time-scroll mt-3 grid grid-cols-2 gap-2 pr-1" :class="compactLayout ? '' : 'xl:block xl:max-h-[390px] xl:space-y-2 xl:overflow-y-auto'">
              <button
                v-for="slot in selectedTimes"
                :key="slot.startsAt"
                type="button"
                class="w-full rounded-lg border bg-white px-3 py-3.5 text-sm font-bold transition hover:text-white"
                :style="timeButtonStyle"
                @mouseenter="setTimeButtonHover($event, true)"
                @mouseleave="setTimeButtonHover($event, false)"
                @focus="setTimeButtonHover($event, true)"
                @blur="setTimeButtonHover($event, false)"
                @click="chooseSlot(slot)"
              >
                {{ formatTime(slot.startsAt) }}
              </button>
            </div>
            <p v-else class="mt-4 rounded-xl bg-zinc-50 p-4 text-sm leading-5 text-zinc-500">No available times for this date.</p>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { ChevronLeftIcon, ChevronRightIcon, ClockIcon, CreditCardIcon, GlobeAltIcon, PhoneArrowUpRightIcon, VideoCameraIcon } from '@heroicons/vue/24/outline';

const props = defineProps({
  settings: { type: Object, default: () => ({}) },
  logoUrl: { type: String, default: '' },
  slots: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  preview: { type: Boolean, default: false },
  compactLayout: { type: Boolean, default: false },
});
const emit = defineEmits(['select-slot']);
const weekdayLabels = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];
const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
const activeDateKey = ref('');
const visibleMonth = ref(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
const accentColor = computed(() => String(props.settings?.accentColor || '#2D8CFF'));
const accentSoft = computed(() => `${accentColor.value}12`);
const durationMinutes = computed(() => Number(props.settings?.durationMinutes || 30));
const priceCents = computed(() => Number(props.settings?.priceCents || 0));
const priceLabel = computed(() => new Intl.NumberFormat('en-US', { style: 'currency', currency: props.settings?.currencyCode || 'USD' }).format(priceCents.value / 100));
const timezoneLabel = computed(() => String(props.settings?.timezone || browserTimezone).replaceAll('_', ' '));

const dateKey = (value) => {
  const parts = new Intl.DateTimeFormat('en-US', { timeZone: browserTimezone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(new Date(value));
  const find = (type) => parts.find((part) => part.type === type)?.value || '';
  return `${find('year')}-${find('month')}-${find('day')}`;
};
const previewSlots = computed(() => {
  if (!props.preview) return [];
  const result = [];
  const cursor = new Date(); cursor.setHours(9, 0, 0, 0);
  for (let offset = 1; offset <= 16; offset += 1) {
    const day = new Date(cursor); day.setDate(cursor.getDate() + offset);
    if ([0, 6].includes(day.getDay())) continue;
    [0, 30, 60, 90, 150, 180, 240].forEach((minutes) => result.push({ startsAt: new Date(day.getTime() + minutes * 60000).toISOString() }));
  }
  return result;
});
const effectiveSlots = computed(() => props.slots.length ? props.slots : previewSlots.value);
const slotsByDate = computed(() => effectiveSlots.value.reduce((groups, slot) => { const key = dateKey(slot.startsAt); (groups[key] ||= []).push(slot); return groups; }, {}));
const availableKeys = computed(() => Object.keys(slotsByDate.value).sort());
const monthLabel = computed(() => new Intl.DateTimeFormat(undefined, { month: 'long', year: 'numeric' }).format(visibleMonth.value));
const calendarCells = computed(() => {
  const first = new Date(visibleMonth.value.getFullYear(), visibleMonth.value.getMonth(), 1);
  const mondayOffset = (first.getDay() + 6) % 7;
  const start = new Date(first); start.setDate(first.getDate() - mondayOffset);
  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(start); date.setDate(start.getDate() + index);
    const key = dateKey(date);
    return { key, label: date.getDate(), date, inMonth: date.getMonth() === visibleMonth.value.getMonth(), available: Boolean(slotsByDate.value[key]) };
  });
});
const selectedTimes = computed(() => slotsByDate.value[activeDateKey.value] || []);
const selectedDateLabel = computed(() => activeDateKey.value ? new Intl.DateTimeFormat(undefined, { weekday: 'long', month: 'long', day: 'numeric' }).format(new Date(`${activeDateKey.value}T12:00:00`)) : 'Choose an available date');
const timeButtonStyle = computed(() => ({ borderColor: `${accentColor.value}80`, color: accentColor.value }));

watch(availableKeys, (keys) => {
  if (!keys.length) { activeDateKey.value = ''; return; }
  if (!keys.includes(activeDateKey.value)) activeDateKey.value = keys[0];
  const first = new Date(`${activeDateKey.value}T12:00:00`);
  visibleMonth.value = new Date(first.getFullYear(), first.getMonth(), 1);
}, { immediate: true });

function moveMonth(amount) { visibleMonth.value = new Date(visibleMonth.value.getFullYear(), visibleMonth.value.getMonth() + amount, 1); }
function selectDate(cell) { if (cell.available) activeDateKey.value = cell.key; }
function chooseSlot(slot) { if (!props.preview) emit('select-slot', slot); }
function setTimeButtonHover(event, active) { const target = event?.currentTarget; if (!target) return; target.style.backgroundColor = active ? accentColor.value : '#FFFFFF'; target.style.color = active ? '#FFFFFF' : accentColor.value; }
function formatTime(value) { return new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit', timeZone: browserTimezone }).format(new Date(value)); }
function calendarDayClass(cell) {
  if (cell.key === activeDateKey.value) return 'font-bold text-white shadow-sm';
  if (cell.available) return 'font-bold hover:brightness-95';
  return cell.inMonth ? 'text-zinc-500 disabled:cursor-default' : 'text-zinc-300 disabled:cursor-default';
}
function calendarDayStyle(cell) {
  if (cell.key === activeDateKey.value) return { backgroundColor: accentColor.value, color: '#FFFFFF' };
  if (cell.available) return { backgroundColor: accentSoft.value, color: accentColor.value };
  return {};
}
</script>

<style scoped>
.meet-time-scroll{scrollbar-width:thin;scrollbar-color:#a1a1aa transparent}.meet-time-scroll::-webkit-scrollbar{width:5px}.meet-time-scroll::-webkit-scrollbar-thumb{border-radius:999px;background:#a1a1aa}
</style>
